import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
    ChevronDown,
    ChevronRight,
    ChevronUp,
    CheckSquare,
    Folder,
    FolderOpen,
    Loader2,
    Square,
    X,
} from 'lucide-react';

import { commonStemTitle, naturalCompare } from '../utils/naturalSort';
import {
    ModalOverlay,
    ModalPanel,
    ModalHeader,
    ModalTitle,
    ModalSubtitle,
    ModalCloseButton,
    ModalBody,
    ModalActions,
    ModalButton,
    ModalInput,
    ModalLabel,
    ModalStatusMessage,
} from './shared/Modal';

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
    animation: ${spin} 0.9s linear infinite;
`;

const DocList = styled.ul`
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
    border: 1px solid #e2e2e2;
    border-radius: 6px;
    max-height: 320px;
    overflow-y: auto;
`;

const DocRow = styled.li<{ $selected?: boolean; $disabled?: boolean; $depth?: number }>`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    padding-left: ${p => 10 + (p.$depth ?? 0) * 18}px;
    font-size: 13px;
    cursor: ${p => (p.$disabled ? 'default' : 'pointer')};
    color: ${p => (p.$disabled ? '#9ca3af' : 'inherit')};
    background: ${p => (p.$selected ? '#eef2ff' : 'transparent')};
    &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
    }
    &:hover {
        background: ${p => (p.$disabled ? 'transparent' : p.$selected ? '#eef2ff' : '#f5f5ff')};
    }
`;

const DocNameWrap = styled.span`
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
`;

const CheckIcon = styled.span`
    flex: 0 0 auto;
    align-self: center;
    display: inline-flex;
    color: #4338ca;
`;

const DocName = styled.span`
    overflow-wrap: anywhere;
`;

const FolderRow = styled.li<{ $depth?: number }>`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    padding-left: ${p => 10 + (p.$depth ?? 0) * 18}px;
    font-size: 13px;
    cursor: pointer;
    color: #374151;
    &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
    }
    &:hover {
        background: #f5f5ff;
    }
`;

const FolderName = styled.span`
    font-weight: 500;
    overflow-wrap: anywhere;
`;

const PulledBadge = styled.span`
    font-size: 11px;
    color: #3f6212;
    background: #ecfccb;
    border: 1px solid #d9f99d;
    border-radius: 4px;
    padding: 1px 6px;
    white-space: nowrap;
    margin-left: 6px;
`;

const DocMeta = styled.span`
    font-size: 12px;
    color: #888;
    white-space: nowrap;
`;

const LoadMoreRow = styled.li`
    padding: 8px 10px;
    font-size: 13px;
    text-align: center;
    color: #4338ca;
    cursor: pointer;
    &:hover {
        background: #f5f5ff;
    }
`;

const InlineHint = styled.li<{ $depth?: number }>`
    padding: 6px 10px;
    padding-left: ${p => 10 + (p.$depth ?? 0) * 18}px;
    font-size: 12px;
    color: #9ca3af;
`;

const Hint = styled.div`
    font-size: 12px;
    color: #888;
    margin-top: 4px;
`;

const ManualToggle = styled.button`
    background: none;
    border: none;
    color: #4338ca;
    text-decoration: underline;
    cursor: pointer;
    font-size: 12px;
    padding: 0;
    align-self: flex-start;
`;

const SEARCH_DEBOUNCE_MS = 300;

const RetryButton = styled.button`
    margin-left: 8px;
    border: 1px solid currentColor;
    border-radius: 6px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 600;
    font-family: inherit;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }
`;

/* ── Grouped pull ("Pull as one document") ── */

const GroupToggleRow = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    user-select: none;

    input {
        cursor: pointer;
    }
`;

const GroupToggleHint = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: #888;
`;

const GroupPanel = styled.div`
    margin-top: 8px;
    border: 1px solid #e2e2e2;
    border-radius: 6px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const GroupPanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 600;
    color: #555;
`;

const GroupSortButton = styled.button`
    border: 1px solid #e2e2e2;
    border-radius: 6px;
    padding: 2px 8px;
    font-size: 11px;
    font-family: inherit;
    background: #fff;
    color: #555;
    cursor: pointer;

    &:hover {
        border-color: #c8c8c8;
    }
`;

const GroupList = styled.ol`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-height: 180px;
    overflow-y: auto;
`;

const GroupRow = styled.li<{ $dragging?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 6px;
    border-radius: 4px;
    font-size: 12px;
    background: ${p => (p.$dragging ? '#eef2ff' : 'transparent')};
    cursor: grab;

    &:hover {
        background: ${p => (p.$dragging ? '#eef2ff' : '#f5f5ff')};
    }
`;

const GroupIndex = styled.span`
    flex-shrink: 0;
    width: 18px;
    text-align: right;
    font-weight: 600;
    color: #888;
`;

const GroupRowName = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const GroupRowMeta = styled.span`
    flex-shrink: 0;
    color: #999;
    font-size: 11px;
`;

const GroupMoveButton = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    border: none;
    background: none;
    padding: 1px;
    color: #888;
    cursor: pointer;

    &:hover:not(:disabled) {
        color: #444;
    }
    &:disabled {
        opacity: 0.3;
        cursor: default;
    }
`;

// Sentinel cache key for the top level of the folder tree.
const ROOT = '__root__';

export interface FilevineDocumentHit {
    documentId: number;
    filename: string;
    folderName: string | null;
    size: number | null;
    uploadDate: string | null;
    /** True when this document has already been pulled into the current case. */
    alreadyPulled: boolean;
}

export interface FilevineDocumentPage {
    documents: FilevineDocumentHit[];
    offset: number;
    hasMore: boolean;
    count: number | null;
}

export interface FilevineFolderHit {
    folderId: number;
    name: string;
}

export interface FilevineFolderLevel {
    /** The folder these contents belong to (null at the flat-fallback root). */
    parentFolderId: number | null;
    folders: FilevineFolderHit[];
    documents: FilevineDocumentHit[];
    /** True when the folder holds more PDFs than the bounded fetch returned. */
    documentsTruncated: boolean;
}

/** What the confirm step queues: every referenced document with the picker's
 *  metadata (real filename + uploadDate, so the import records true
 *  provenance), plus an optional ORDERED group to import as ONE combined
 *  document (fragments of a single artifact, e.g. a transcript split across
 *  scans). */
export interface FilevinePullSubmission {
    documents: Array<{ documentId: number; filename?: string | null; uploadDate?: string | null }>;
    group?: { documentIds: number[]; title?: string | null } | null;
}

export interface FilevinePullModalProps {
    /** Fetch one page of the linked project's documents (flat filtered search). */
    onList: (params: { q?: string; offset?: number }) => Promise<FilevineDocumentPage>;
    /** Fetch one level of the folder tree (root when parentFolderId is omitted). */
    onListFolders: (parentFolderId?: number) => Promise<FilevineFolderLevel>;
    /** Queue the pull of the selected Filevine documents into the current case. */
    onConfirm: (submission: FilevinePullSubmission) => Promise<void> | void;
    onClose: () => void;
    /** Project label for context (e.g. "Smith" or "project 46774103"). */
    projectLabel?: string | null;
}

const formatSize = (bytes: number | null): string | null => {
    if (bytes === null || !Number.isFinite(bytes)) return null;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
};

const formatDate = (iso: string | null): string | null => {
    if (!iso) return null;
    const m = iso.match(/^\d{4}-\d{2}-\d{2}/);
    return m ? m[0] : null;
};

/**
 * "Pull Filevine documents into this case" picker. Two ways to find documents:
 *  - a lazy directory tree (the default) that mirrors the project's Filevine
 *    folder structure, fetching each folder's contents only when it's expanded;
 *  - a filename filter that overlays the tree with a flat server-side search.
 * Multiple documents can be selected (across folders) and pulled at once —
 * each becomes its own FILEVINE_IMPORT job, or, with "Pull as one document"
 * enabled, ONE job imports the ordered selection as ONE combined document
 * (fragments of a single artifact, e.g. a transcript split across scans).
 * Manual id entry stays available as a fallback. The modal just queues and
 * closes; the shared jobs indicator tracks progress.
 */
/**
 * Turn a failed pull request into a message that says WHY. The server's
 * ``detail`` (a string, or FastAPI's list of validation errors — e.g. the
 * per-request document cap) is far more useful than a generic retry prompt.
 */
export const describeQueueError = (err: any): string => {
    const status: number | undefined = err?.response?.status;
    const detail = err?.response?.data?.detail;
    let reason: string | undefined;
    if (typeof detail === 'string') {
        reason = detail;
    } else if (Array.isArray(detail)) {
        reason = detail
            .map(item => {
                const loc = Array.isArray(item?.loc) ? item.loc.filter((p: unknown) => typeof p === 'string').join('.') : '';
                const msg = typeof item?.msg === 'string' ? item.msg : JSON.stringify(item);
                return loc ? `${loc}: ${msg}` : msg;
            })
            .join('; ');
    }
    if (reason) {
        return status === 422 ? `The server rejected this pull: ${reason}` : reason;
    }
    if (!err?.response) return 'Failed to queue the pull: the server did not respond. Please try again.';
    return `Failed to queue the pull (HTTP ${status ?? '?'}). Please try again.`;
};

export const FilevinePullModal: React.FC<FilevinePullModalProps> = ({
    onList,
    onListFolders,
    onConfirm,
    onClose,
    projectLabel,
}) => {
    const [query, setQuery] = useState('');
    // Shared selection across the tree and the flat search, keyed by document
    // id. A parallel map keeps the hit objects so confirm can pass metadata.
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const hitsById = useRef<Map<number, FilevineDocumentHit>>(new Map());
    const [manualMode, setManualMode] = useState(false);
    const [manualValue, setManualValue] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ---- Grouped pull ("Pull as one document") ----
    // groupOrder is the ORDERED member list shown in the panel; it follows the
    // selection (natural-sorted inserts) but preserves manual reordering.
    // Grouping defaults ON for multi-select: one job = one pipeline pass over
    // all fragments, avoiding the per-document model-server switching that
    // makes N separate small imports slow — and boundary inference re-splits
    // the combined artifact at real document boundaries anyway. Unchecking is
    // respected for the life of the modal.
    const [groupMode, setGroupMode] = useState(true);
    const [groupOrder, setGroupOrder] = useState<number[]>([]);
    const [groupTitle, setGroupTitle] = useState('');
    const [titleTouched, setTitleTouched] = useState(false);
    const [dragId, setDragId] = useState<number | null>(null);

    // Deterministic default order: natural filename sort, then uploadDate,
    // then Filevine document id (mechanical splits upload monotonically).
    const compareHits = useCallback((aId: number, bId: number): number => {
        const a = hitsById.current.get(aId);
        const b = hitsById.current.get(bId);
        const byName = naturalCompare(a?.filename ?? String(aId), b?.filename ?? String(bId));
        if (byName !== 0) return byName;
        const ad = a?.uploadDate ?? '';
        const bd = b?.uploadDate ?? '';
        if (ad !== bd) {
            if (ad === '') return 1;
            if (bd === '') return -1;
            return ad < bd ? -1 : 1;
        }
        return aId - bId;
    }, []);

    // Keep the ordered member list in sync with the selection: keep the
    // existing (possibly hand-reordered) sequence, insert newly selected docs
    // at their natural-sort position, drop deselected ones.
    useEffect(() => {
        if (!groupMode) return;
        setGroupOrder(prev => {
            const kept = prev.filter(id => selectedIds.has(id));
            const added = Array.from(selectedIds).filter(id => !kept.includes(id));
            if (added.length === 0 && kept.length === prev.length) return prev;
            const next = [...kept];
            for (const id of [...added].sort(compareHits)) {
                const idx = next.findIndex(existing => compareHits(id, existing) < 0);
                if (idx === -1) next.push(id);
                else next.splice(idx, 0, id);
            }
            return next;
        });
    }, [groupMode, selectedIds, compareHits]);

    // Prefill the combined-document title from the members' common stem until
    // the operator edits it.
    useEffect(() => {
        if (!groupMode || titleTouched) return;
        const names = groupOrder
            .map(id => hitsById.current.get(id)?.filename)
            .filter((n): n is string => Boolean(n));
        setGroupTitle(commonStemTitle(names) ?? '');
    }, [groupMode, groupOrder, titleTouched]);

    const moveGroupMember = useCallback((id: number, delta: number) => {
        setGroupOrder(prev => {
            const idx = prev.indexOf(id);
            const target = idx + delta;
            if (idx === -1 || target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            next.splice(idx, 1);
            next.splice(target, 0, id);
            return next;
        });
    }, []);

    const handleGroupDragOver = useCallback((overId: number) => {
        if (dragId === null || dragId === overId) return;
        setGroupOrder(prev => {
            const next = prev.filter(id => id !== dragId);
            const idx = next.indexOf(overId);
            if (idx === -1) return prev;
            next.splice(idx, 0, dragId);
            return next;
        });
    }, [dragId]);

    const term = query.trim();
    const searching = term !== '';

    const rememberHit = useCallback((hit: FilevineDocumentHit) => {
        hitsById.current.set(hit.documentId, hit);
    }, []);

    const toggleSelect = useCallback((hit: FilevineDocumentHit) => {
        if (hit.alreadyPulled) return;
        rememberHit(hit);
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(hit.documentId)) next.delete(hit.documentId);
            else next.add(hit.documentId);
            return next;
        });
    }, [rememberHit]);

    // ---- Flat search state (active only when the filter is non-empty) ----
    const [docs, setDocs] = useState<FilevineDocumentHit[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [count, setCount] = useState<number | null>(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [listError, setListError] = useState<string | null>(null);
    const listSeq = useRef(0);

    useEffect(() => {
        if (manualMode || !searching) return;
        const seq = ++listSeq.current;
        setSearchLoading(true);
        const timer = window.setTimeout(async () => {
            try {
                const page = await onList({ q: term });
                if (listSeq.current !== seq) return; // superseded
                page.documents.forEach(rememberHit);
                setDocs(page.documents);
                setHasMore(page.hasMore);
                setCount(page.count);
                setListError(null);
            } catch (e) {
                if (listSeq.current !== seq) return;
                setDocs([]);
                setHasMore(false);
                setListError(
                    'Could not search the project’s documents. You can still browse the folders or enter a document id manually.',
                );
            } finally {
                if (listSeq.current === seq) setSearchLoading(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [term, searching, manualMode, onList, rememberHit]);

    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        const seq = listSeq.current;
        try {
            const page = await onList({ q: term, offset: docs.length });
            if (listSeq.current !== seq) return; // filter changed meanwhile
            page.documents.forEach(rememberHit);
            setDocs(prev => [...prev, ...page.documents]);
            setHasMore(page.hasMore);
            setCount(page.count);
        } catch (e) {
            setListError('Loading more documents failed. Try again.');
        } finally {
            setLoadingMore(false);
        }
    };

    // ---- Tree state (active when the filter is empty) ----
    // Cache of each folder's loaded contents, keyed by folder id (or the
    // sentinel ROOT for the top level). expanded tracks which folders are open.
    const [levels, setLevels] = useState<Map<string, FilevineFolderLevel>>(new Map());
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set());
    const [treeError, setTreeError] = useState<string | null>(null);
    // ONE in-flight load per folder key. Every path that wants a level —
    // seed, prefetch, folder click, retry — goes through loadLevel, which
    // reuses the running load for that key instead of starting a duplicate.
    // A user-visible request arriving while a SILENT prefetch is in flight
    // "promotes" it (spinner + error visibility) rather than re-fetching:
    // previously a click superseded a nearly-done prefetch and re-queued the
    // whole request, which is exactly what made folder expands hang.
    const inFlight = useRef<Map<string, Promise<void>>>(new Map());
    const promoted = useRef<Set<string>>(new Set());
    // Root failure/retry: the first failure schedules ONE automatic retry —
    // the failed attempt usually warmed the backend's Filevine auth/DNS, so
    // the retry tends to succeed fast; after that the error UI's Retry button
    // re-arms the seed via rootAttempt.
    const rootFailures = useRef(0);
    const rootRetryTimer = useRef<number | null>(null);
    const [rootAttempt, setRootAttempt] = useState(0);

    const loadLevel = useCallback(
        (key: string, parentFolderId?: number, opts?: { silent?: boolean }): Promise<void> => {
            const silent = opts?.silent ?? false;
            const existing = inFlight.current.get(key);
            if (existing) {
                // Reuse the running load for this key. A user-visible request
                // promotes a silent prefetch instead of duplicating it.
                if (!silent && !promoted.current.has(key)) {
                    promoted.current.add(key);
                    setLoadingFolders(prev => new Set(prev).add(key));
                    if (key === ROOT) setTreeError(null);
                }
                return existing;
            }
            if (!silent) {
                promoted.current.add(key);
                setLoadingFolders(prev => new Set(prev).add(key));
                if (key === ROOT) setTreeError(null);
            }
            const run = (async () => {
                // Visibility is decided at COMPLETION time: a silent prefetch
                // may have been promoted by a click while it was in flight.
                const visible = () => promoted.current.has(key);
                try {
                    const level = await onListFolders(parentFolderId);
                    // Single writer per key (in-flight dedupe), so this can
                    // never clobber a newer load's result.
                    setLevels(prev => new Map(prev).set(key, level));
                    level.documents.forEach(rememberHit);
                    if (key === ROOT && visible()) setTreeError(null);
                } catch (e) {
                    // Unpromoted prefetches fail silently — the folder simply
                    // loads (with visible feedback) when clicked.
                    if (key === ROOT && visible()) {
                        rootFailures.current += 1;
                        setTreeError(
                            'Could not load the project’s folders. You can retry, search by filename, or enter a document id manually.',
                        );
                        if (rootFailures.current === 1) {
                            rootRetryTimer.current = window.setTimeout(() => {
                                setRootAttempt(a => a + 1);
                            }, 2000);
                        }
                    }
                } finally {
                    inFlight.current.delete(key);
                    if (visible()) {
                        setLoadingFolders(prev => {
                            const next = new Set(prev);
                            next.delete(key);
                            return next;
                        });
                    }
                    promoted.current.delete(key);
                }
            })();
            inFlight.current.set(key, run);
            return run;
        },
        [onListFolders, rememberHit],
    );

    // Prefetch the ROOT's immediate children (one level deep) so the common
    // "open modal → expand a top folder" is usually a warm cache hit. Best
    // effort and silent: a failed prefetch simply loads on click as before.
    const prefetchChildren = useCallback(
        (folders: FilevineFolderHit[]) => {
            for (const folder of folders) {
                const key = String(folder.folderId);
                if (levels.has(key)) {
                    continue;
                }
                // loadLevel dedupes in-flight loads per key, so firing for a
                // folder that is already loading is a no-op.
                void loadLevel(key, folder.folderId, { silent: true });
            }
        },
        [levels, loadLevel],
    );

    // Seed the root level when the tree view is shown (and again on retry via
    // rootAttempt). loadLevel dedupes per key, so an effect re-fire while the
    // root is already loading simply attaches to the running load.
    useEffect(() => {
        if (manualMode || searching) return;
        if (levels.has(ROOT)) return;
        void loadLevel(ROOT, undefined);
    }, [manualMode, searching, levels, loadLevel, rootAttempt]);

    // Clear a pending auto-retry timer on unmount.
    useEffect(() => () => {
        if (rootRetryTimer.current !== null) {
            window.clearTimeout(rootRetryTimer.current);
        }
    }, []);

    // Once the root level is loaded, kick off child prefetches.
    useEffect(() => {
        const root = levels.get(ROOT);
        if (root && root.folders.length > 0) {
            prefetchChildren(root.folders);
        }
    }, [levels, prefetchChildren]);

    const toggleFolder = useCallback(
        (folderId: number) => {
            setExpanded(prev => {
                const next = new Set(prev);
                if (next.has(folderId)) {
                    next.delete(folderId);
                } else {
                    next.add(folderId);
                    const key = String(folderId);
                    if (!levels.has(key)) {
                        // Dedupe/promote lives inside loadLevel: if a silent
                        // prefetch is already fetching this folder, the click
                        // attaches to it (with a spinner) instead of
                        // re-fetching from scratch.
                        void loadLevel(key, folderId);
                    }
                }
                return next;
            });
        },
        [levels, loadLevel],
    );

    const manualId = Number.parseInt(manualValue.trim(), 10);
    const manualValid =
        manualValue.trim() !== '' && Number.isInteger(manualId) && manualId > 0;
    const selectedCount = selectedIds.size;
    const groupActive = groupMode && selectedCount >= 2;
    const canConfirm = manualMode ? manualValid : selectedCount > 0;

    // Every referenced document with the picker's metadata, so the import
    // records the real filename + uploadDate for ALL pulls (not just singles).
    const buildDocumentsMeta = (ids: number[]) =>
        ids.map(id => {
            const hit = hitsById.current.get(id);
            return {
                documentId: id,
                filename: hit?.filename ?? null,
                uploadDate: hit?.uploadDate ?? null,
            };
        });

    const handleConfirm = async () => {
        if (!canConfirm || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            if (manualMode) {
                await onConfirm({ documents: [{ documentId: manualId }] });
            } else if (groupActive) {
                await onConfirm({
                    documents: buildDocumentsMeta(groupOrder),
                    group: { documentIds: groupOrder, title: groupTitle.trim() || null },
                });
            } else {
                await onConfirm({ documents: buildDocumentsMeta(Array.from(selectedIds)) });
            }
            onClose();
        } catch (e) {
            setError(describeQueueError(e));
            setSubmitting(false);
        }
    };

    // Render a single document leaf row (shared by tree + search).
    const renderDoc = (doc: FilevineDocumentHit, depth: number) => {
        const isSelected = selectedIds.has(doc.documentId);
        return (
            <DocRow
                key={`d${doc.documentId}`}
                $selected={isSelected}
                $disabled={doc.alreadyPulled}
                $depth={depth}
                onClick={() => toggleSelect(doc)}
                title={
                    doc.alreadyPulled
                        ? `Already pulled into this case (Filevine document id ${doc.documentId})`
                        : `Filevine document id ${doc.documentId}`
                }
            >
                <DocNameWrap>
                    {!doc.alreadyPulled && (
                        <CheckIcon>
                            {isSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                        </CheckIcon>
                    )}
                    <DocName>
                        {doc.filename}
                        {doc.alreadyPulled && <PulledBadge>already pulled</PulledBadge>}
                    </DocName>
                </DocNameWrap>
                <DocMeta>
                    {[
                        // In the tree the folder is implicit, so drop folderName there.
                        depth === 0 ? doc.folderName : null,
                        formatSize(doc.size),
                        formatDate(doc.uploadDate),
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </DocMeta>
            </DocRow>
        );
    };

    // Recursively render one folder level's folders + documents.
    const renderLevel = (key: string, depth: number): React.ReactNode => {
        const level = levels.get(key);
        if (!level) {
            return loadingFolders.has(key) ? (
                <InlineHint key={`${key}-loading`} $depth={depth}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Spinner size={12} /> Loading…
                    </span>
                </InlineHint>
            ) : null;
        }
        const rows: React.ReactNode[] = [];
        for (const folder of level.folders) {
            const open = expanded.has(folder.folderId);
            rows.push(
                <FolderRow
                    key={`f${folder.folderId}`}
                    $depth={depth}
                    onClick={() => toggleFolder(folder.folderId)}
                    title={folder.name}
                >
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {open ? <FolderOpen size={15} /> : <Folder size={15} />}
                    <FolderName>{folder.name}</FolderName>
                </FolderRow>,
            );
            if (open) {
                rows.push(
                    <React.Fragment key={`f${folder.folderId}-children`}>
                        {renderLevel(String(folder.folderId), depth + 1)}
                    </React.Fragment>,
                );
            }
        }
        for (const doc of level.documents) {
            rows.push(renderDoc(doc, depth));
        }
        if (level.documentsTruncated) {
            rows.push(
                <InlineHint key={`${key}-truncated`} $depth={depth}>
                    More documents in this folder — filter by filename to find them.
                </InlineHint>,
            );
        }
        if (level.folders.length === 0 && level.documents.length === 0 && !level.documentsTruncated) {
            rows.push(
                <InlineHint key={`${key}-empty`} $depth={depth}>
                    (empty)
                </InlineHint>,
            );
        }
        return rows;
    };

    return (
        <ModalOverlay onClick={onClose}>
            <ModalPanel $width="560px" onClick={e => e.stopPropagation()}>
                <ModalHeader>
                    <div>
                        <ModalTitle>Pull from Filevine</ModalTitle>
                        <ModalSubtitle>
                            {projectLabel
                                ? `Append documents from ${projectLabel} into this case. Only PDFs are shown.`
                                : 'Append Filevine documents into this case. Only PDFs are shown.'}
                        </ModalSubtitle>
                    </div>
                    <ModalCloseButton onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </ModalCloseButton>
                </ModalHeader>
                <ModalBody>
                    {!manualMode ? (
                        <>
                            <ModalLabel htmlFor="filevine-doc-filter">
                                {searching
                                    ? `Search results${count !== null ? ` (${count} in project)` : ''}`
                                    : 'Browse folders or filter by filename'}
                            </ModalLabel>
                            <ModalInput
                                id="filevine-doc-filter"
                                placeholder="Filter by filename…"
                                value={query}
                                autoFocus
                                autoComplete="off"
                                onChange={e => setQuery(e.target.value)}
                            />
                            {searching ? (
                                // ---- Flat search overlay ----
                                searchLoading ? (
                                    <Hint>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            <Spinner size={12} />
                                            Searching…
                                        </span>
                                    </Hint>
                                ) : docs.length > 0 ? (
                                    <DocList>
                                        {docs.map(doc => renderDoc(doc, 0))}
                                        {hasMore && (
                                            <LoadMoreRow onClick={() => void loadMore()}>
                                                {loadingMore ? 'Loading…' : 'Load more'}
                                            </LoadMoreRow>
                                        )}
                                    </DocList>
                                ) : (
                                    !listError && <Hint>No documents matched.</Hint>
                                )
                            ) : (
                                // ---- Directory tree (default) ----
                                loadingFolders.has(ROOT) && !levels.has(ROOT) ? (
                                    <Hint>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            <Spinner size={12} />
                                            Loading folders…
                                        </span>
                                    </Hint>
                                ) : (
                                    <DocList>{renderLevel(ROOT, 0)}</DocList>
                                )
                            )}
                            {(listError || treeError) && (
                                <ModalStatusMessage $tone="error">
                                    {searching ? listError : treeError}
                                    {!searching && treeError && (
                                        <RetryButton
                                            type="button"
                                            onClick={() => setRootAttempt(a => a + 1)}
                                        >
                                            Retry
                                        </RetryButton>
                                    )}
                                </ModalStatusMessage>
                            )}
                            {selectedCount >= 2 && (
                                <GroupToggleRow>
                                    <input
                                        type="checkbox"
                                        checked={groupMode}
                                        onChange={e => setGroupMode(e.target.checked)}
                                    />
                                    Pull as one document
                                    <GroupToggleHint>
                                        fragments of a single artifact, imported in order
                                    </GroupToggleHint>
                                </GroupToggleRow>
                            )}
                            {groupActive && (
                                <GroupPanel>
                                    <GroupPanelHeader>
                                        <span>Import order — drag or use the arrows to fix it</span>
                                        <GroupSortButton
                                            type="button"
                                            onClick={() => setGroupOrder(prev => [...prev].sort(compareHits))}
                                        >
                                            Sort by name
                                        </GroupSortButton>
                                    </GroupPanelHeader>
                                    <GroupList>
                                        {groupOrder.map((id, index) => {
                                            const hit = hitsById.current.get(id);
                                            return (
                                                <GroupRow
                                                    key={id}
                                                    $dragging={dragId === id}
                                                    draggable
                                                    onDragStart={() => setDragId(id)}
                                                    onDragEnd={() => setDragId(null)}
                                                    onDragOver={e => {
                                                        e.preventDefault();
                                                        handleGroupDragOver(id);
                                                    }}
                                                >
                                                    <GroupIndex>{index + 1}.</GroupIndex>
                                                    <GroupRowName title={hit?.filename ?? `document ${id}`}>
                                                        {hit?.filename ?? `document ${id}`}
                                                    </GroupRowName>
                                                    <GroupRowMeta>
                                                        {[formatSize(hit?.size ?? null), formatDate(hit?.uploadDate ?? null)]
                                                            .filter(Boolean)
                                                            .join(' · ')}
                                                    </GroupRowMeta>
                                                    <GroupMoveButton
                                                        type="button"
                                                        aria-label={`Move ${hit?.filename ?? id} up`}
                                                        disabled={index === 0}
                                                        onClick={() => moveGroupMember(id, -1)}
                                                    >
                                                        <ChevronUp size={14} />
                                                    </GroupMoveButton>
                                                    <GroupMoveButton
                                                        type="button"
                                                        aria-label={`Move ${hit?.filename ?? id} down`}
                                                        disabled={index === groupOrder.length - 1}
                                                        onClick={() => moveGroupMember(id, 1)}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </GroupMoveButton>
                                                </GroupRow>
                                            );
                                        })}
                                    </GroupList>
                                    <div>
                                        <ModalLabel htmlFor="filevine-group-title">
                                            Combined document title
                                        </ModalLabel>
                                        <ModalInput
                                            id="filevine-group-title"
                                            value={groupTitle}
                                            placeholder="Title for the combined document"
                                            onChange={e => {
                                                setTitleTouched(true);
                                                setGroupTitle(e.target.value);
                                            }}
                                        />
                                    </div>
                                </GroupPanel>
                            )}
                            <ManualToggle onClick={() => setManualMode(true)}>
                                Enter a document id manually
                            </ManualToggle>
                        </>
                    ) : (
                        <>
                            <ModalLabel htmlFor="filevine-doc-id">Filevine document id</ModalLabel>
                            <ModalInput
                                id="filevine-doc-id"
                                type="number"
                                inputMode="numeric"
                                placeholder="e.g. 46774103"
                                value={manualValue}
                                autoFocus
                                onChange={e => setManualValue(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') void handleConfirm();
                                }}
                            />
                            <ManualToggle onClick={() => setManualMode(false)}>
                                Back to the document browser
                            </ManualToggle>
                        </>
                    )}
                    {error && <ModalStatusMessage $tone="error">{error}</ModalStatusMessage>}
                </ModalBody>
                <ModalActions>
                    <ModalButton $variant="secondary" onClick={onClose} disabled={submitting}>
                        Cancel
                    </ModalButton>
                    <ModalButton
                        $variant="primary"
                        onClick={() => void handleConfirm()}
                        disabled={!canConfirm || submitting}
                    >
                        {submitting
                            ? 'Queuing…'
                            : manualMode
                              ? 'Pull document'
                              : groupActive
                                ? `Pull ${selectedCount} documents as one`
                                : selectedCount > 1
                                  ? `Pull ${selectedCount} documents`
                                  : 'Pull document'}
                    </ModalButton>
                </ModalActions>
            </ModalPanel>
        </ModalOverlay>
    );
};
